'use client';

import { useEffect, useRef } from 'react';
import { getGeocodeReverseFn } from '@/api/ahafood/get-revert-address-fn';
import { resolveCityFromCoordinates } from '@/lib/fast-location-utils';
import { scheduleParamRemoval } from '@/lib/url-cleaner';
import { useAiFoodOrderStore } from '@/store';

/**
 * Handle `?lat=&lng=` URL params (WebInApp pass-through hoặc deep link).
 *
 * Priority HIGHEST — override existing address store.
 *
 * Flow:
 *  1. Read URL ngay khi mount
 *  2. `setIsLoading(true)` → chờ `resolveCityFromCoordinates` (API city_detail)
 *     → `setCityId` + address chính xác (city_id luôn có nhờ fallback)
 *  3. Async reverse geocode để upgrade address string (fire-and-forget)
 *  4. Clear lat/lng từ URL sau khi đọc
 */
export function UrlLocationHandler() {
  const setAddress = useAiFoodOrderStore.use.setAddress();
  const setCityId = useAiFoodOrderStore.use.setCityId();
  const setSkipAutoLocation = useAiFoodOrderStore.use.setSkipAutoLocation();
  const setIsLoading = useAiFoodOrderStore.use.setIsLoading();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const url = new URL(window.location.href);
    const latStr = url.searchParams.get('lat');
    const lngStr = url.searchParams.get('lng');

    if (!latStr || !lngStr) return;

    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    void (async () => {
      setIsLoading(true);
      try {
        // 1. Chờ API xác định city chính xác trước khi set
        const city = await resolveCityFromCoordinates(lat, lng);
        setCityId(city.value);

        setAddress({
          lat,
          lng,
          address: city.label,
          place_id: `${lat}_${lng}`,
        });
        setSkipAutoLocation(false);

        // 2. Schedule clear lat/lng (batched với token + webinapp qua microtask)
        scheduleParamRemoval('lat', 'lng');

        // 3. Async reverse geocode để upgrade address string
        void getGeocodeReverseFn({ lat, lng })
          .then((fullAddress) => {
            if (fullAddress) setAddress(fullAddress);
          })
          .catch(() => {
            /* ignore — provisional address vẫn ok */
          });
      } catch {
        /* ignore — URL parsing fail không block app */
      } finally {
        setIsLoading(false);
      }
    })();
  }, [setAddress, setCityId, setSkipAutoLocation, setIsLoading]);

  return null;
}
